import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const LS_KEY = "cf_project_filter";

const ProjectFilterContext = createContext({
  project: "all",
  setProject: () => {},
  projects: [],
  setProjects: () => {},
});

/**
 * Global project filter. Persisted via URL (?project=Name) as source of truth,
 * with localStorage as a soft memory across sessions.
 *
 * IMPORTANT: uses explicit `useNavigate` + `useLocation.pathname` (not
 * `useSearchParams.setSearchParams`) so the pathname is always the current
 * one — previously, `setSearchParams` was captured inside a `useMemo` closure
 * and could resolve against a stale pathname, redirecting users from e.g.
 * /planning to / when they picked a filter.
 */
export function ProjectFilterProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const urlProject = searchParams.get("project");
  const [project, setProjectState] = useState(
    urlProject || (typeof window !== "undefined" ? window.localStorage.getItem(LS_KEY) : null) || "all",
  );
  const [projects, setProjects] = useState([]);

  // Sync URL → state (browser back/forward, deep links)
  useEffect(() => {
    if (urlProject && urlProject !== project) {
      setProjectState(urlProject);
    } else if (!urlProject && project !== "all") {
      // Re-hydrate URL from state when navigating to a page without ?project
      const sp = new URLSearchParams(location.search);
      sp.set("project", project);
      navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true });
    }
    // Only depends on URL state — intentionally omitting `project` and `navigate`
  }, [location.pathname, location.search, urlProject]);

  const setProject = useCallback(
    (value) => {
      setProjectState(value);
      try { window.localStorage.setItem(LS_KEY, value); } catch { /* ignore */ }
      const sp = new URLSearchParams(window.location.search);
      if (value === "all") sp.delete("project");
      else sp.set("project", value);
      // Always preserve the current pathname — read from window.location.pathname
      // at CALL TIME so we don't capture a stale value in this callback.
      const currentPath = window.location.pathname;
      navigate({ pathname: currentPath, search: sp.toString() ? `?${sp.toString()}` : "" }, { replace: true });
    },
    [navigate],
  );

  const ctx = useMemo(() => ({ project, setProject, projects, setProjects }), [project, projects, setProject]);
  return <ProjectFilterContext.Provider value={ctx}>{children}</ProjectFilterContext.Provider>;
}

export function useProjectFilter() {
  return useContext(ProjectFilterContext);
}
